import React from 'react';
import { Box, Typography, alpha, styled } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SurfaceSection, { SurfaceSectionProps } from './SurfaceSection';
import { opacity } from '../AppTheme/themePrimitives';

export type ActionCardProps = Omit<SurfaceSectionProps, 'title'> & {
    title: string;
    accentColor?: string;
    description?: string;
    disabled?: boolean;
    disabledLabel?: string;
    icon?: React.ReactNode;
    indicator?: React.ReactNode;
};

const DisabledOverlay = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.common.black, 0.75),
    color: theme.palette.common.white,
    cursor: 'not-allowed',
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    opacity: 0,
    padding: theme.spacing(2),
    position: 'absolute',
    textAlign: 'center',
    transition: 'opacity 0.2s ease',
    '&:hover': {
        opacity: 1,
    },
}));

const ClickableSurface = styled(SurfaceSection, {
    shouldForwardProp: (prop) => prop !== 'clickable' && prop !== 'isDisabled' && prop !== 'accentColor',
})<{ accentColor?: string; clickable: boolean; isDisabled: boolean }>(({ accentColor, clickable, isDisabled, theme }) => {
    const color = accentColor ?? theme.palette.primary.main;
    return {
        cursor: isDisabled ? 'not-allowed' : clickable ? 'pointer' : 'default',
        opacity: isDisabled ? opacity.disabled : 1,
        overflow: 'hidden',
        position: 'relative',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        '&&': {
            padding: theme.spacing(2),
        },
        ...(!isDisabled && clickable && {
            '&:hover': {
                backgroundColor: alpha(color, opacity.hoverSurface),
                borderColor: alpha(color, opacity.hoverBorder),
            },
        }),
    };
});

const ContentBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
}));

const HeaderRow = styled(Box)({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
});

const IconAndTitle = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
}));

const IconBadge = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'accentColor',
})<{ accentColor?: string }>(({ accentColor, theme }) => {
    const color = accentColor ?? theme.palette.primary.main;
    return {
        alignItems: 'center',
        backgroundColor: alpha(color, opacity.iconBadge),
        borderRadius: theme.shape.borderRadius,
        color,
        display: 'flex',
        flexShrink: 0,
        height: theme.spacing(4.5),
        justifyContent: 'center',
        width: theme.spacing(4.5),
    };
});

const ArrowIcon = styled(ArrowForwardIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
    flexShrink: 0,
}));

export default function ActionCard({ title, accentColor, description, disabled, disabledLabel, icon, indicator = <ArrowIcon fontSize="small" />, onClick, ...props }: ActionCardProps) {
    return (
        <ClickableSurface accentColor={accentColor} clickable={!!onClick} isDisabled={!!disabled} onClick={onClick} {...props}>
            <ContentBox>
                <HeaderRow>
                    <IconAndTitle>
                        <IconBadge accentColor={accentColor}>{icon}</IconBadge>
                        <Typography variant="h6">{title}</Typography>
                    </IconAndTitle>
                    {onClick && indicator}
                </HeaderRow>
                {description && (
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                )}
            </ContentBox>
            {disabled && disabledLabel && (
                <DisabledOverlay>
                    <Typography variant="caption">{disabledLabel}</Typography>
                </DisabledOverlay>
            )}
        </ClickableSurface>
    );
}
